var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
var __spreadArray = this && this.__spreadArray || function (to, from, pack) {
  if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
    if (ar || !(i in from)) {
      if (!ar) ar = Array.prototype.slice.call(from, 0, i);
      ar[i] = from[i];
    }
  }
  return to.concat(ar || Array.prototype.slice.call(from));
};
var _a, _b, _c, _d, _e;
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { StatusPermission } from '../../enum';
import { ErrorMessageBox, TableBody, TableCell, TableRow } from '../common';
import { ExpandableRowLabel } from '../common/tableHierarchy';
import { useExportAPI, usePrintTableAPI, useReportEffectivePermission } from '../report/selectors';
import { AnnualTable, AnnualTableBody, AnnualTableHead, DefaultActionCell, FirstCell, STICKIES } from './annualTable';
import { getImplicitNestedExpansionItems } from './common/annualSectionTable';
import { MultigeoLobRow } from './common/multigeoQueuesByLob';
import { getQueueExpansionKeysForLob, useSelectQueuesByLobPerScenario, useTableRowExpansion } from './common/selectors';
import { getHighlightBorder, getScenarioCellColor } from './common/utils';
import { ANNUAL_REPORT_ROWS, DECIMAL_VALUE, HIERARCHY_LEVEL, NEST_LEVEL, PERCENTAGES, TOTAL_ROW_COLOR, getLobExpansionKey, getPrefixedExpansionKey } from './constants';
import { SeatsRequiredTable } from './seatsRequired';
import { useSummaryByDisplayMode, useSummaryChunkSize, useSummaryReportAPI } from './selectors';
import { SupportStaffTable } from './supportStaff';
import { ConstraintType } from './types';
var SECTION_QUEUE_KEYS = [ConstraintType.DefQueue, ConstraintType.NonDefQueue];
var getSectionKeys = function (rows) {
  var keys = SECTION_QUEUE_KEYS.flatMap(function (key) {
    var _a;
    return (_a = rows[key]) !== null && _a !== void 0 ? _a : [];
  });
  return Array.from(new Set(keys));
};
var getTemplateLob = function (lob) {
  var _a, _b;
  return {
    lobName: lob.lobName,
    defQueues: (_a = lob.defQueues) !== null && _a !== void 0 ? _a : [],
    nonDefQueues: (_b = lob.nonDefQueues) !== null && _b !== void 0 ? _b : []
  };
};
var PROD_HOURS_TABLE_CONFIG = {
  occupancy: {
    title: 'reportRows.occupancy',
    unit: '%',
    rows: (_a = {}, _a[ConstraintType.DefQueue] = ['occupancy'], _a[ConstraintType.NonDefQueue] = ['occupancy'], _a),
    hideLocation: true,
    nestLevelOffset: 1
  },
  attrition: {
    title: 'reportRows.attrition',
    unit: function (t) {
      return "# ".concat(t('ofAgents'));
    },
    rows: (_b = {}, _b[ConstraintType.DefQueue] = ['attrition'], _b[ConstraintType.NonDefQueue] = ['attrition'], _b),
    nestLevelOffset: 1
  },
  productiveHours: {
    title: 'reportRows.productiveHours',
    unit: function (t) {
      return t('hours');
    },
    rows: (_c = {}, _c[ConstraintType.DefQueue] = ['productiveHours'], _c[ConstraintType.NonDefQueue] = ['productiveHours'], _c),
    nestLevelOffset: 1
  },
  productionHours: {
    title: 'productionHours',
    unit: function (t) {
      return t('hours');
    },
    rows: (_d = {}, _d[ConstraintType.DefQueue] = ['productionHours'], _d[ConstraintType.NonDefQueue] = ['productionHours'], _d),
    nestLevelOffset: 1
  },
  agentRequirement: {
    title: 'agentRequirement',
    unit: function (t) {
      return "# ".concat(t('ofAgents'));
    },
    rows: (_e = {}, _e[ConstraintType.DefQueue] = ['productionFte', 'requiredFte', 'requiredHeadcount'], _e[ConstraintType.NonDefQueue] = ['productionFte', 'requiredFte', 'requiredHeadcount'], _e),
    both: false
  }
};
var ProdHoursActionCell = function (props) {
  return _jsx(DefaultActionCell, __assign({}, props, {
    showMultiskillAction: false,
    showHeadcountAction: false
  }));
};
var ProdHoursCollapsibleRow = function (_a) {
  var keyItem = _a.keyItem,
    data = _a.data,
    chunkSize = _a.chunkSize,
    lobsPerScenario = _a.lobsPerScenario,
    expanded = _a.expanded,
    onToggle = _a.onToggle,
    config = _a.config,
    expandedItems = _a.expandedItems,
    onToggleItem = _a.onToggleItem;
  var t = useTranslation('report', {
    keyPrefix: 'annual'
  }).t;
  return _jsxs(_Fragment, {
    children: [_jsxs(TableRow, {
      children: [_jsx(FirstCell, __assign({
        "aria-label": NEST_LEVEL[HIERARCHY_LEVEL.total],
        stickySizes: STICKIES,
        bold: true,
        color: TOTAL_ROW_COLOR
      }, {
        children: _jsx(ExpandableRowLabel, __assign({
          expanded: expanded,
          onToggle: onToggle,
          bold: true
        }, {
          children: t("reportRows.".concat(ANNUAL_REPORT_ROWS[keyItem]))
        }))
      })), data.map(function (scenario, index, arr) {
        var _a;
        return _jsx(TableCell, {
          colSpan: 2,
          value: scenario[keyItem],
          decimal: (_a = DECIMAL_VALUE[keyItem]) !== null && _a !== void 0 ? _a : 0,
          percentage: PERCENTAGES.includes(keyItem),
          bold: true,
          color: getScenarioCellColor(scenario),
          highlightBorder: getHighlightBorder(index, arr.length, chunkSize),
          useColorValue: true
        }, "".concat(keyItem, "-").concat(scenario.name, "-").concat(index));
      })]
    }), expanded && lobsPerScenario.map(function (lobEntry) {
      var _a, _b;
      return _jsx(MultigeoLobRow, {
        lob: getTemplateLob(lobEntry.template),
        perScenarioLob: lobEntry.perScenarioLob,
        data: data,
        chunkSize: chunkSize,
        keyItem: keyItem,
        hideConstraintHeader: (_a = config.hideConstraintHeader) !== null && _a !== void 0 ? _a : true,
        hideLocationRows: (_b = config.hideLocation) !== null && _b !== void 0 ? _b : false,
        expandedItems: expandedItems,
        onToggleItem: onToggleItem,
        expansionKeyPrefix: keyItem,
        nestLevelOffset: config.nestLevelOffset
      }, "".concat(keyItem, "-").concat(lobEntry.lobName));
    })]
  });
};
var ProdHoursMultigeoSection = function (_a) {
  var _b;
  var config = _a.config;
  var t = useTranslation('report', {
    keyPrefix: 'annual'
  }).t;
  var data = useSummaryByDisplayMode();
  var chunkSize = useSummaryChunkSize();
  var lobsPerScenario = useSelectQueuesByLobPerScenario();
  var keys = getSectionKeys(config.rows);
  var lobs = useMemo(function () {
    return lobsPerScenario.map(function (_a) {
      var lobName = _a.lobName;
      return getLobExpansionKey(lobName);
    });
  }, [lobsPerScenario]);
  var hasKeySections = keys.length > 1;
  var getHierarchyItemsForKey = useCallback(function (keyItem) {
    return __spreadArray(__spreadArray([], lobs.map(function (lob) {
      return getPrefixedExpansionKey(keyItem, lob);
    }), true), lobsPerScenario.flatMap(function (lobEntry) {
      return getQueueExpansionKeysForLob(lobEntry, keyItem);
    }), true);
  }, [lobs, lobsPerScenario]);
  var hierarchyItems = useMemo(function () {
    return keys.flatMap(getHierarchyItemsForKey);
  }, [getHierarchyItemsForKey, keys]);
  var queueExpansion = useTableRowExpansion(hierarchyItems);
  var sectionKeyExpansion = useTableRowExpansion(keys);
  var handleHierarchyToggleItem = function (itemName) {
    var expanded = !queueExpansion.expandedItems[itemName];
    var nestedItems = getImplicitNestedExpansionItems(itemName, hierarchyItems);
    queueExpansion.setItemsExpanded([itemName], expanded);
    queueExpansion.setItemsExpanded(nestedItems, false);
  };
  var hasHierarchy = !config.disableHierarchy && !!(data === null || data === void 0 ? void 0 : data.some(function (scenario) {
    var _a;
    return (_a = scenario.queuesByLob) === null || _a === void 0 ? void 0 : _a.some(function (lob) {
      return lob.defQueues.length || lob.nonDefQueues.length;
    });
  }));
  var title = t(config.title);
  var unit = typeof config.unit === 'function' ? config.unit(t) : config.unit;
  var handleExpandToggle = function () {
    if (!hasKeySections) {
      queueExpansion.toggleAll();
      return;
    }
    var expanded = sectionKeyExpansion.allCollapsed;
    sectionKeyExpansion.setItemsExpanded(keys, expanded);
    queueExpansion.setItemsExpanded(hierarchyItems, expanded);
  };
  return _jsxs(_Fragment, {
    children: [_jsx(AnnualTableHead, {
      title: title,
      unit: unit,
      isExpandable: hasHierarchy,
      expanded: hasKeySections ? sectionKeyExpansion.someExpanded : queueExpansion.someExpanded,
      onExpandToggle: handleExpandToggle
    }), (!data || !hasHierarchy) && _jsx(AnnualTableBody, {
      headerRows: config.rows,
      both: (_b = config.both) !== null && _b !== void 0 ? _b : true,
      hideConstraintHeader: true
    }), !!data && hasHierarchy && _jsx(TableBody, {
      children: keys.map(function (keyItem) {
        return hasKeySections ? _jsx(ProdHoursCollapsibleRow, {
          keyItem: keyItem,
          data: data,
          chunkSize: chunkSize,
          lobsPerScenario: lobsPerScenario,
          expanded: sectionKeyExpansion.expandedItems[keyItem],
          onToggle: function () {
            var expanded = !sectionKeyExpansion.expandedItems[keyItem];
            sectionKeyExpansion.setItemsExpanded([keyItem], expanded);
            queueExpansion.setItemsExpanded(getHierarchyItemsForKey(keyItem), false);
          },
          config: config,
          expandedItems: queueExpansion.expandedItems,
          onToggleItem: handleHierarchyToggleItem
        }, "".concat(config.title, "-").concat(keyItem)) : _jsx(React.Fragment, {
          children: lobsPerScenario.map(function (lobEntry) {
            var _a, _b;
            return _jsx(MultigeoLobRow, {
              lob: getTemplateLob(lobEntry.template),
              perScenarioLob: lobEntry.perScenarioLob,
              data: data,
              chunkSize: chunkSize,
              keyItem: keyItem,
              hideConstraintHeader: (_a = config.hideConstraintHeader) !== null && _a !== void 0 ? _a : true,
              hideLocationRows: (_b = config.hideLocation) !== null && _b !== void 0 ? _b : false,
              expandedItems: queueExpansion.expandedItems,
              onToggleItem: handleHierarchyToggleItem,
              expansionKeyPrefix: keyItem,
              nestLevelOffset: config.nestLevelOffset
            }, "".concat(keyItem, "-").concat(lobEntry.lobName));
          })
        }, keyItem);
      })
    })]
  });
};
export var ProdHoursResourcingTable = function (_a) {
  var handleOpenDialog = _a.handleOpenDialog;
  var t = useTranslation('report', {
    keyPrefix: 'annual'
  }).t;
  var summaryData = useSummaryReportAPI()[0];
  var printData = usePrintTableAPI()[0];
  var exportData = useExportAPI()[0];
  var capacityPermission = useReportEffectivePermission();
  var summaryByDisplayMode = useSummaryByDisplayMode();
  // Check if all occupancy values are 100% (or 1 in decimal format)
  var shouldHideProductiveHours = useMemo(function () {
    if (!summaryByDisplayMode || summaryByDisplayMode.length === 0) {
      return false;
    }
    var allOccupancies = [];
    summaryByDisplayMode.forEach(function (report) {
      if (report.defQueue) {
        report.defQueue.forEach(function (queue) {
          if (queue.occupancy !== undefined && queue.occupancy !== null) {
            allOccupancies.push(queue.occupancy);
          }
        });
      }
      if (report.nonDefQueue) {
        report.nonDefQueue.forEach(function (queue) {
          if (queue.occupancy !== undefined && queue.occupancy !== null) {
            allOccupancies.push(queue.occupancy);
          }
        });
      }
    });
    // If no occupancy values found, don't hide
    if (allOccupancies.length === 0) {
      return false;
    }
    // Check if all occupancy values are 100% (or 1 in decimal format)
    return allOccupancies.every(function (occ) {
      return occ === 100 || occ === 1;
    });
  }, [summaryByDisplayMode]);
  var sections = Object.entries(PROD_HOURS_TABLE_CONFIG).filter(function (_a) {
    var key = _a[0];
    return !(key === 'productiveHours' && shouldHideProductiveHours);
  });
  return _jsxs(_Fragment, {
    children: [_jsx(ErrorMessageBox, {
      errors: [summaryData.error, printData.error, exportData.error]
    }), _jsxs(AnnualTable, {
      children: [_jsx(AnnualTableHead, {
        handleOpenDialog: handleOpenDialog,
        title: t('resourcingSummary'),
        scenarioNameHeader: true,
        permission: capacityPermission === StatusPermission.Edit
      }), _jsx(AnnualTableBody, {
        handleOpenDialog: handleOpenDialog,
        headerRows: {},
        actionCellComponent: ProdHoursActionCell
      }), sections.map(function (_a) {
        var key = _a[0],
          config = _a[1];
        return _jsx(ProdHoursMultigeoSection, {
          config: config
        }, key);
      }), _jsx(SeatsRequiredTable, {}), _jsx(SupportStaffTable, {})]
    })]
  });
};